import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/apiClient";
import { STATUS_META, formatDate } from "@/lib/status";
import { toast } from "sonner";
import AvailabilityPicker from "@/components/app/AvailabilityPicker";
import {
  Wind, MapPin, ShieldCheck, ShieldX, CalendarClock, Wrench, CheckCircle2, Loader2, Hash, Building2, ClipboardList, ChevronRight, Phone, MessageCircle, Circle,
} from "lucide-react";

const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

function techWa(phone) {
  let p = (phone || "").replace(/\D/g, "");
  if (p && !p.startsWith("55")) p = "55" + p;
  return `https://wa.me/${p}`;
}

function TrackingDialog({ open, onOpenChange, data }) {
  const meta = data ? (STATUS_META[data.status] || { label: data.status, color: "bg-slate-100 text-slate-700 border-slate-200" }) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="public-tracking-dialog">
        <DialogHeader>
          <DialogTitle>Acompanhar chamado</DialogTitle>
          <DialogDescription>{data?.equipment_label || ""}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Status atual</span>
              <Badge className={`${meta.color} border`} data-testid="tracking-status">{meta.label}</Badge>
            </div>
            {data.technician_name && (
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-xs text-slate-400">Técnico responsável</p>
                <p className="font-medium text-slate-800">{data.technician_name}</p>
                {data.technician_phone && (
                  <div className="flex gap-2 mt-2">
                    <a href={`tel:${data.technician_phone}`} className="flex-1" data-testid="tracking-call">
                      <button className="w-full flex items-center justify-center gap-1.5 rounded-md border border-slate-200 py-1.5 text-sm text-slate-700 hover:bg-slate-50"><Phone className="h-4 w-4" /> Ligar</button>
                    </a>
                    <a href={techWa(data.technician_phone)} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid="tracking-whatsapp">
                      <button className="w-full flex items-center justify-center gap-1.5 rounded-md border border-green-500 py-1.5 text-sm text-green-700 hover:bg-green-50"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
                    </a>
                  </div>
                )}
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-[#0a2a6c] uppercase tracking-wide mb-2">Andamento</p>
              <div className="space-y-3" data-testid="tracking-timeline">
                {data.timeline?.map((h, i) => {
                  const hm = STATUS_META[h.status] || { label: h.status };
                  return (
                    <div key={i} className="flex gap-2">
                      <Circle className={`h-3 w-3 mt-1 flex-shrink-0 ${i === data.timeline.length - 1 ? "text-[#1e7be0] fill-[#1e7be0]" : "text-slate-300 fill-slate-300"}`} />
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700">{hm.label}</p>
                        <p className="text-xs text-slate-400">{formatDate(h.at)}</p>
                        {h.note && <p className="text-xs text-slate-500">{h.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
      <span className="text-slate-500">{label}:</span>
      <span className="text-slate-800 font-medium">{value}</span>
    </div>
  );
}

export default function PublicEquipment() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [form, setForm] = useState({ name: "", phone: "", email: "", description: "", availability: "", is_warranty: false, availability_date: "", availability_period: "", availability_special: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [newReqId, setNewReqId] = useState(null);
  const [track, setTrack] = useState(null);
  const [trackData, setTrackData] = useState(null);

  const openTracking = async (id) => {
    setTrack(id);
    setTrackData(null);
    try {
      const { data } = await publicApi.get(`/public/request/${id}`);
      setTrackData(data);
    } catch {
      toast.error("Não foi possível carregar o chamado.");
    }
  };

  useEffect(() => {
    publicApi.get(`/public/equipment/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.availability) {
      toast.error("Preencha nome, telefone e disponibilidade."); return;
    }
    setBusy(true);
    try {
      const { data } = await publicApi.post(`/public/equipment/${token}/request`, form);
      setNewReqId(data.id);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (data === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (data === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center" data-testid="public-eq-notfound">
        <img src="/logo-clima-branca-icon.png" alt="Clima Branca" className="h-16 w-16 rounded-xl mb-4" />
        <p className="text-slate-600 font-medium">Equipamento não encontrado.</p>
        <p className="text-sm text-slate-400">Verifique o QR Code e tente novamente.</p>
      </div>
    );
  }

  const label = [data.equipment_type, data.brand, data.model].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-equipment-page">
      <header className="bg-gradient-to-r from-[#0a2a6c] to-[#1e7be0] text-white">
        <div className="max-w-lg mx-auto px-5 py-5 flex items-center gap-3">
          <img src="/logo-clima-branca-icon.png" alt="Clima Branca" className="h-11 w-11 rounded-lg ring-2 ring-white/30" />
          <div className="leading-tight">
            <p className="font-bold">Clima Branca</p>
            <p className="text-xs text-white/70 uppercase tracking-widest">Franquias</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4">
        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Wind className="h-6 w-6 text-[#1e7be0]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-800">{label}</h1>
                {data.company && <p className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{data.company}</p>}
              </div>
            </div>

            {data.warranty_status && (
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border ${data.warranty_status === "vigente" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}
                data-testid="public-warranty-seal">
                {data.warranty_status === "vigente" ? <ShieldCheck className="h-4 w-4" /> : <ShieldX className="h-4 w-4" />}
                Garantia {data.warranty_status === "vigente" ? "VIGENTE" : "VENCIDA"}
                {data.warranty?.end ? ` · até ${data.warranty.end}` : ""}
                {data.warranty?.type ? ` (${data.warranty.type === "fabrica" ? "fábrica" : "serviço"})` : ""}
              </div>
            )}

            <div className="space-y-1.5 pt-1">
              <Row icon={MapPin} label="Local" value={data.location} />
              <Row icon={Hash} label="Nº de série" value={data.serial_number} />
              <Row icon={Wind} label="Capacidade" value={data.capacity_btu ? `${data.capacity_btu} BTU` : null} />
              <Row icon={CalendarClock} label="Instalação" value={data.install_date} />
              <Row icon={CalendarClock} label="Última manutenção" value={data.last_maintenance_date} />
              {data.warranty?.coverage && <Row icon={ShieldCheck} label="Cobertura" value={data.warranty.coverage} />}
            </div>
          </CardContent>
        </Card>

        {data.pmoc_history?.length > 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#0a2a6c] uppercase tracking-wide mb-3">Histórico de manutenção</h2>
              <div className="space-y-2" data-testid="public-pmoc-history">
                {data.pmoc_history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm border-l-2 border-blue-100 pl-3">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-slate-700">{h.service_date}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{h.technician_name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {data.tickets_history?.length > 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-[#0a2a6c] uppercase tracking-wide mb-3 flex items-center gap-2">
                <ClipboardList className="h-4 w-4" /> Histórico de atendimentos
              </h2>
              <div className="space-y-2" data-testid="public-tickets-history">
                {data.tickets_history.map((t, i) => {
                  const meta = STATUS_META[t.status] || { label: t.status, color: "bg-slate-100 text-slate-700 border-slate-200" };
                  return (
                    <button key={i} type="button" onClick={() => t.id && openTracking(t.id)}
                      className="w-full flex items-center justify-between gap-2 text-sm border-l-2 border-blue-100 pl-3 py-1 text-left hover:bg-slate-50 rounded-r"
                      data-testid={`public-ticket-${i}`}>
                      <div className="min-w-0">
                        <span className="text-slate-700">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : ""}
                          {t.service_type ? ` · ${t.service_type}` : ""}
                        </span>
                        {t.technician_name && <p className="text-xs text-slate-400">Atendido por {t.technician_name}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Badge className={`${meta.color} border text-xs`}>{meta.label}</Badge>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200">
          <CardContent className="p-5">
            {done ? (
              <div className="text-center py-6" data-testid="public-request-success">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-semibold text-slate-800">Chamado aberto com sucesso!</p>
                <p className="text-sm text-slate-500 mt-1">Nossa equipe entrará em contato em breve.</p>
                {newReqId && (
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => openTracking(newReqId)} data-testid="public-track-button">
                    <ClipboardList className="h-4 w-4" /> Acompanhar chamado
                  </Button>
                )}
              </div>
            ) : (
              <>
                <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-1"><Wrench className="h-4 w-4 text-[#1e7be0]" /> Abrir chamado</h2>
                <p className="text-sm text-slate-500 mb-4">Solicite um atendimento para este equipamento.</p>
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5"><Label>Seu nome *</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="public-name-input" /></div>
                    <div className="space-y-1.5"><Label>Telefone *</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(21) 99999-9999" data-testid="public-phone-input" /></div>
                  </div>
                  <div className="space-y-1.5">
                    <AvailabilityPicker
                      value={{ availability_date: form.availability_date, availability_period: form.availability_period, availability_special: form.availability_special }}
                      onChange={(v) => setForm({ ...form, ...v })} testidPrefix="public-availability" />
                  </div>
                  <div className="space-y-1.5"><Label>Descrição do problema</Label>
                    <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descreva o que está acontecendo" data-testid="public-description-input" /></div>
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <Label className="text-sm">É atendimento em garantia?</Label>
                    <Switch checked={form.is_warranty} onCheckedChange={(v) => setForm({ ...form, is_warranty: v })} data-testid="public-warranty-switch" />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="public-submit-button">
                    {busy ? "Enviando..." : "Abrir chamado"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 pb-6">© {new Date().getFullYear()} Clima Branca Franquias</p>
      </main>
      <TrackingDialog open={!!track} onOpenChange={(o) => !o && setTrack(null)} data={trackData} />
    </div>
  );
}
